/**
 * Core React components for vibesites
 */

import { type ReactNode, type ComponentType, Suspense } from 'react';
import type { ComponentSlot, PageSection } from '../types';
import { useComponent, useHasComponent } from './hooks';
import { useVibesites } from './context';
import { useSSRErrorHandler } from '../ssr-context';
import { SectionContextProvider } from './section-context';

// ============================================================================
// Slot Renderer
// ============================================================================

export interface SlotProps {
  /** Component slot to render */
  slot: ComponentSlot;
  /** Props to pass to the component */
  props?: Record<string, unknown>;
  /** Fallback while loading */
  fallback?: ReactNode;
  /** Content to show if slot is empty */
  empty?: ReactNode;
}

/**
 * Renders a component from the registry by slot name.
 * When an SSR error handler is present (server-side rendering), wraps
 * the component render in try/catch so incompatible components degrade
 * to empty placeholder divs instead of crashing the entire page.
 */
export function Slot({ slot, props = {}, fallback, empty }: SlotProps) {
  const Component = useComponent(slot);
  const ssrErrorHandler = useSSRErrorHandler();

  if (!Component) {
    return empty ? <>{empty}</> : null;
  }

  if (ssrErrorHandler) {
    try {
      return (
        <div data-vibesite-slot={slot} style={{ display: 'contents' }}>
          <Component {...props} />
        </div>
      );
    } catch (err) {
      ssrErrorHandler(slot, err);
      return <div data-vibesite-slot={slot} data-ssr-skipped="true" />;
    }
  }

  if (fallback) {
    return (
      <div data-vibesite-slot={slot} style={{ display: 'contents' }}>
        <Suspense fallback={fallback}>
          <Component {...props} />
        </Suspense>
      </div>
    );
  }

  return (
    <div data-vibesite-slot={slot} style={{ display: 'contents' }}>
      <Component {...props} />
    </div>
  );
}

// ============================================================================
// Section Renderer
// ============================================================================

export interface SectionProps {
  /** Section configuration */
  section: PageSection;
  /** Optional className for the section wrapper */
  className?: string;
}

/**
 * Renders a page section with visibility handling.
 */
export function Section({ section, className }: SectionProps) {
  const context = useVibesites();
  
  // Check visibility
  let isVisible = true;
  if (typeof section.visible === 'function') {
    isVisible = section.visible(context);
  } else if (typeof section.visible === 'boolean') {
    isVisible = section.visible;
  }

  if (!isVisible) {
    return null;
  }

  const isBlock = section.slot.startsWith('block:');

  return (
    <section id={section.id} className={className}>
      {isBlock ? (
        <SectionContextProvider sectionId={section.id}>
          <Slot slot={section.slot} props={section.props} />
        </SectionContextProvider>
      ) : (
        <Slot slot={section.slot} props={section.props} />
      )}
    </section>
  );
}

// ============================================================================
// Page Renderer
// ============================================================================

export interface PageRendererProps {
  /** Sections to render */
  sections: PageSection[];
  /** Wrapper component for all sections */
  wrapper?: ComponentType<{ children: ReactNode }>;
  /** Class name for section wrappers */
  sectionClassName?: string;
}

/**
 * Renders a complete page from section definitions.
 */
export function PageRenderer({ 
  sections, 
  wrapper: Wrapper,
  sectionClassName,
}: PageRendererProps) {
  const content = (
    <>
      {sections.map((section) => (
        <Section 
          key={section.id} 
          section={section} 
          className={sectionClassName}
        />
      ))}
    </>
  );

  if (Wrapper) {
    return <Wrapper>{content}</Wrapper>;
  }

  return content;
}

// ============================================================================
// Conditional Components
// ============================================================================

export interface ShowIfProps {
  /** Condition to check */
  condition: boolean | (() => boolean);
  /** Content to show if condition is true */
  children: ReactNode;
  /** Content to show if condition is false */
  fallback?: ReactNode;
}

/**
 * Conditionally renders children based on a condition.
 */
export function ShowIf({ condition, children, fallback }: ShowIfProps) {
  const isTrue = typeof condition === 'function' ? condition() : condition;
  
  if (isTrue) {
    return <>{children}</>;
  }
  
  return fallback ? <>{fallback}</> : null;
}

export interface ShowSlotIfProps {
  /** Component slot to check and render */
  slot: ComponentSlot;
  /** Props to pass to the component */
  props?: Record<string, unknown>;
  /** Content to show if slot is not available */
  fallback?: ReactNode;
}

/**
 * Shows a slot only if the component is available in the registry.
 */
export function ShowSlotIf({ slot, props, fallback }: ShowSlotIfProps) {
  const hasComponent = useHasComponent(slot);
  
  if (hasComponent) {
    return <Slot slot={slot} props={props} />;
  }
  
  return fallback ? <>{fallback}</> : null;
}

// ============================================================================
// Layout Components
// ============================================================================

export interface LayoutProps {
  children: ReactNode;
}

/**
 * Default layout that includes header and footer slots.
 */
export function DefaultLayout({ children }: LayoutProps) {
  return (
    <div className="vibesites-layout">
      <Slot slot="Header" />
      <main className="vibesites-main">
        {children}
      </main>
      <Slot slot="Footer" />
    </div>
  );
}

// ============================================================================
// SEO Component
// ============================================================================

export interface SEOProps {
  /** Page title */
  title: string;
  /** Page description */
  description?: string;
  /** Canonical URL */
  canonicalUrl?: string;
  /** Open Graph image */
  ogImage?: string;
}

/**
 * SEO meta tags component.
 * Note: For Next.js, use their Head component instead.
 */
export function SEO({ title: _title, description: _description, canonicalUrl: _canonicalUrl, ogImage: _ogImage }: SEOProps) {
  // This would typically integrate with a head management library
  // For now, this is a placeholder that should be replaced per-framework
  return null;
}

